// The public venue listing — the player app's Venues list and venue page (spec/08 §4.3,
// F-ORG-01 rule 6; the screens are F-MOB-P-05, F-MOB-P-06 and F-MOB-P-21).
//
//   GET /v1/public/venues          unauthenticated
//   GET /v1/public/venues/{slug}   unauthenticated
//
// "These serve a **separate projection**, not a stripped version of the authenticated resource":
// the listing fields and nothing else — never a station identity, a player, a session or a member.
// "Only venues with `publicListing` and status `live` appear", and "ordering honours the
// `featured`, `partner` and `sponsored` flags". The responses carry a short `Cache-Control`, so a
// busy venue costs nothing to list.
//
// What is **not** here yet, and why: the passes and membership plans on sale, and per station
// class `free` of `total`, the waitlist length and an estimated wait. Those come from the pricing,
// memberships and stations modules and from the Valkey floor read model, none of which exists in
// M1; the `titleId` and `classId` filters need the venue library and the stations likewise. They
// are the follow-up named in the F-ORG-01 pull request, and they add fields — additive, not
// breaking (spec/08 §2.1).
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { VenueIdSchema } from "../ids/schema.js";
import { AddressSchema, CoordinateSchema } from "../primitives/address.js";
import { HolidaysSchema, OpeningHoursSchema } from "../primitives/opening-hours.js";
import { PAGE_DEFAULT_LIMIT, PAGE_MAX_LIMIT, paginated } from "../primitives/page.js";
import { TimezoneSchema } from "../primitives/scalars.js";
import { commonProblemResponses, JSON_CONTENT_TYPE, problemResponse } from "../openapi/responses.js";
import { NonNegativeMoneySchema } from "../primitives/money.js";
import { StationPassSchema } from "./passes.js";
import { VENUE_AMENITIES, VenueAmenitiesSchema, VenueAmenitySchema, VenueContactSchema, VenuePhotosSchema, VenueSlugSchema, } from "./venues.js";
export const PublicVenueSchema = z
    .object({
    id: VenueIdSchema,
    slug: VenueSlugSchema,
    name: z.string(),
    timezone: TimezoneSchema,
    address: AddressSchema.nullable(),
    coordinate: CoordinateSchema.nullable(),
    contact: VenueContactSchema,
    openingHours: OpeningHoursSchema,
    holidays: HolidaysSchema,
    amenities: VenueAmenitiesSchema,
    photos: VenuePhotosSchema,
    /** Computed at the moment of the request, in the venue's timezone, from its hours and holidays. */
    openNow: z.boolean(),
    featured: z.boolean(),
    partner: z.boolean(),
    sponsored: z.boolean(),
    /**
     * Straight-line distance from `lat`/`lng` in whole metres, when the request gave them. An integer,
     * like every quantity on the wire but a coordinate (the station's generated DTOs refuse a float).
     */
    distanceMeters: z.int().min(0).nullable(),
})
    .openapi('PublicVenue', { description: "A venue's public listing. A projection, not the venue resource." });
export const PublicVenueListSchema = paginated(PublicVenueSchema, 'PublicVenueList');
/**
 * `amenities=parking,food`: every listed amenity must be offered. A plain string on the wire (a
 * query parameter is one), checked against the amenity list here so an unknown one is a 400.
 */
const AMENITY_LIST = new RegExp(`^(?:${VENUE_AMENITIES.join('|')})(?:,(?:${VENUE_AMENITIES.join('|')}))*$`);
const AmenityListParamSchema = z
    .string()
    .regex(AMENITY_LIST, `Comma-separated, from: ${VENUE_AMENITIES.join(', ')}`)
    .openapi({ description: 'Comma-separated; every one must be offered.', example: 'parking,food' });
const BooleanParamSchema = z.enum(['true', 'false']);
/** The amenities a query parameter names, parsed. */
export function parseAmenityList(value) {
    if (value === undefined)
        return [];
    return [...new Set(value.split(','))].map((part) => VenueAmenitySchema.parse(part));
}
export const PublicVenueQuerySchema = z
    .object({
    city: z.string().trim().min(1).max(80).optional().openapi({ description: 'The address locality, case-insensitive.' }),
    lat: z.coerce.number().min(-90).max(90).optional(),
    lng: z.coerce.number().min(-180).max(180).optional(),
    radiusKm: z.coerce
        .number()
        .int()
        .min(1)
        .max(200)
        .optional()
        .openapi({ description: 'Whole kilometres, with lat and lng. Default 10.' }),
    openNow: BooleanParamSchema.optional(),
    amenities: AmenityListParamSchema.optional(),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(PAGE_MAX_LIMIT).default(PAGE_DEFAULT_LIMIT),
})
    // Unknown parameters are refused, never turned into a filter (spec/08 §2.4).
    .strict()
    .refine((query) => (query.lat === undefined) === (query.lng === undefined), {
    message: 'Send lat and lng together.',
})
    .refine((query) => query.radiusKm === undefined || query.lat !== undefined, {
    message: 'radiusKm needs lat and lng.',
})
    .openapi('PublicVenueQuery');
const PublicVenueParamsSchema = z.object({ slug: VenueSlugSchema }).openapi('PublicVenueParams');
const cached = (description, schema) => ({
    description,
    headers: z.object({ 'Cache-Control': z.string() }),
    content: { [JSON_CONTENT_TYPE]: { schema } },
});
export const listPublicVenuesRoute = createRoute({
    method: 'get',
    path: '/v1/public/venues',
    operationId: 'listPublicVenues',
    tags: ['public'],
    summary: 'Find venues',
    description: 'Unauthenticated. Only listed, live venues. Ordered Featured, then Sponsored, then Partner, then by ' +
        'name. Filters: `city`, or `lat`/`lng`/`radiusKm`; `amenities`; `openNow`.',
    request: { query: PublicVenueQuerySchema },
    responses: {
        200: cached('A page of listed venues.', PublicVenueListSchema),
        400: commonProblemResponses[400],
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getPublicVenueRoute = createRoute({
    method: 'get',
    path: '/v1/public/venues/{slug}',
    operationId: 'getPublicVenue',
    tags: ['public'],
    summary: 'A venue page',
    description: 'Unauthenticated. A venue that is not listed, or not live, is 404 — the same as one that does not exist.',
    request: { params: PublicVenueParamsSchema },
    responses: {
        200: cached('The venue’s public listing.', PublicVenueSchema),
        400: commonProblemResponses[400],
        404: problemResponse('No listed venue with that slug.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// What a venue sells and how busy it is (F-MOB-P-21, ADR-0054; spec/15 P-06)
// ---------------------------------------------------------------------------------------------
//
// The player web's venue page: the passes on sale with their real prices, the pay-as-you-go rate per kind of PC, and
// how many PCs are free now. Display data for a player deciding where to go; buying is at the counter until an online
// rail is live (`onlinePayments` says which). Never a station's identity, a player or a session.
/** One kind of PC at the venue: how many, how many are free now, and what an hour costs. */
export const PublicStationClassSchema = z
    .object({
    name: z.string(),
    /** PCs of this kind in service. */
    total: z.int().min(0),
    /** Of those, ready for a new player now: not in a session, not under maintenance or switched off by staff. */
    free: z.int().min(0),
    /** What an hour of pay as you go costs now; `null` when the venue sells play by passes only. */
    hourlyRate: NonNegativeMoneySchema.nullable(),
})
    .openapi('PublicStationClass');
export const PublicVenueShopSchema = z
    .object({
    /** The passes on sale here, cheapest first, with the kinds of PC each is for. */
    passes: z.array(StationPassSchema),
    stationClasses: z.array(PublicStationClassSchema),
    /** Whether a player can pay online here; `false` means pay at the counter. */
    onlinePayments: z.boolean(),
})
    .openapi('PublicVenueShop', { description: 'What a venue sells and how many PCs are free, for its public page.' });
export const getPublicVenueShopRoute = createRoute({
    method: 'get',
    path: '/v1/public/venues/{slug}/shop',
    operationId: 'getPublicVenueShop',
    tags: ['public'],
    summary: 'A venue’s passes, prices and free PCs',
    description: 'Unauthenticated. The passes on sale with their prices, the pay-as-you-go rate per kind of PC and how many are free ' +
        'now. A venue that is not listed, or not live, is 404.',
    request: { params: PublicVenueParamsSchema },
    responses: {
        200: cached('What the venue sells now.', PublicVenueShopSchema),
        400: commonProblemResponses[400],
        404: problemResponse('No listed venue with that slug.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=public-venues.js.map